import Glide from "@glidejs/glide";
import useNcId from "hooks/useNcId";
import { FC, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import ScrollDownIndicator from "./ScrollDownIndicator";

export interface SectionHeroBCRProps {
  className?: string;
}

const heroSlides = [
  {
    image:
      "https://images.unsplash.com/photo-1489515217757-5fd1be406fef?auto=format&fit=crop&w=2200&q=80",
    title: "Trusted Across the GCC",
    subtitle: "Supplying landmark projects across the Middle East",
    href: "/projects",
  },
  {
    image:
      "https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=2200&q=80",
    title: "Expansion Joint Covers",
    subtitle: "Premium aluminium & stainless steel movement systems",
    href: "/categories/37",
  },
  {
    image:
      "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?auto=format&fit=crop&w=2200&q=80",
    title: "Shadow Gap Profiles",
    subtitle: "Precision-engineered profiles for modern architecture",
    href: "/categories/3",
  },
  {
    image:
      "https://images.unsplash.com/photo-1517581177682-a085bb7ffb15?auto=format&fit=crop&w=2200&q=80",
    title: "Luxury Tile Trims",
    subtitle: "Aluminium, SS & brass trim series for every finish",
    href: "/categories/28",
  },
  {
    image:
      "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=2200&q=80",
    title: "Skirting & LED Profiles",
    subtitle: "Complete floor-to-wall finishing solutions",
    href: "/categories/11",
  },
];

const SectionHeroBCR: FC<SectionHeroBCRProps> = ({ className = "" }) => {
  const UNIQUE_CLASS = "HeroBCR" + useNcId();
  const [activeIndex, setActiveIndex] = useState(0);
  const glideRef = useRef<Glide | null>(null);
  const slide = heroSlides[activeIndex] ?? heroSlides[0];

  useEffect(() => {
    const container = document.querySelector(`.${UNIQUE_CLASS}`);
    const slides = container?.querySelectorAll(".glide__slide");
    if (!container || !slides || slides.length === 0) return;

    const timer = setTimeout(() => {
      glideRef.current?.destroy();
      const instance = new Glide(`.${UNIQUE_CLASS}`, {
        type: "carousel",
        perView: 1,
        gap: 0,
        autoplay: 5000,
        hoverpause: true,
        animationDuration: 900,
      });
      instance.mount();
      instance.on("run.after", () => setActiveIndex(instance.index));
      glideRef.current = instance;
    }, 80);

    return () => {
      clearTimeout(timer);
      glideRef.current?.destroy();
      glideRef.current = null;
    };
  }, [UNIQUE_CLASS]);

  return (
    <section className={`bcr-hero relative w-full overflow-hidden ${className}`}>
      <div className={`${UNIQUE_CLASS} absolute inset-0`}>
        <div className="glide__track h-full" data-glide-el="track">
          <ul className="glide__slides h-full">
            {heroSlides.map((item, i) => (
              <li key={i} className="glide__slide h-full">
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full object-cover"
                  loading={i === 0 ? "eager" : "lazy"}
                />
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="absolute inset-0 bg-gradient-to-r from-[#0a1428]/95 via-[#0f1b3d]/80 to-[#0f1b3d]/45 z-[1]" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0a1428]/90 via-[#0a1428]/20 to-[#0a1428]/40 z-[1]" />

      <div className="relative z-10 h-full flex items-center">
        <div className="container px-4 sm:px-6 lg:px-8 py-24 sm:py-28 lg:py-32">
          <div className="max-w-3xl">
            <span className="inline-block px-3 py-1 mb-5 text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase rounded-full bg-white/10 text-white/90 border border-white/20 backdrop-blur-sm">
              UK Quality · UAE Stock
            </span>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-[1.1] tracking-tight">
              High-Quality{" "}
              <span className="text-primary-300">BCR Profiles</span> for Every
              Industry
            </h1>

            <p className="mt-5 text-sm sm:text-base lg:text-lg text-white/75 leading-relaxed max-w-2xl">
              Durable, safe, and precision-engineered metal profiles for
              construction, manufacturing, and architectural projects across the
              UAE and GCC.
            </p>

            <div
              key={activeIndex}
              className="mt-6 p-4 sm:p-5 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm max-w-xl animate-[fadeIn_0.5s_ease-out]"
            >
              <p className="text-xs uppercase tracking-wider text-primary-300 font-semibold">
                Featured
              </p>
              <h2 className="mt-1 text-lg sm:text-xl font-semibold text-white">
                {slide.title}
              </h2>
              <p className="mt-1 text-sm text-white/65">{slide.subtitle}</p>
              <Link
                to={slide.href}
                className="inline-flex items-center mt-3 text-sm font-semibold text-primary-300 hover:text-white transition-colors"
              >
                Learn more →
              </Link>
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/categories" className="bcr-btn-primary text-sm px-6 py-3">
                Explore Products
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center px-6 py-3 text-sm font-semibold rounded-full border-2 border-white/40 text-white hover:bg-white/10 transition-all"
              >
                Get a Quote
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-28 sm:bottom-32 left-1/2 -translate-x-1/2 z-20 flex gap-2">
        {heroSlides.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => glideRef.current?.go(`=${i}`)}
            className={`h-2 rounded-full transition-all duration-300 ${
              activeIndex === i
                ? "w-8 bg-white"
                : "w-2 bg-white/45 hover:bg-white/70"
            }`}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>

      <ScrollDownIndicator className="absolute bottom-6 sm:bottom-8 left-1/2 -translate-x-1/2 z-20" />
    </section>
  );
};

export default SectionHeroBCR;
